/**
 * Shared @ mention popover. Used both by the typeahead (initial insert) and by
 * GapAssignee/GapDate chips when re-edited.
 *
 * Groups (in order): Dates, Team, Client (when set). Filtering is case-insensitive
 * substring match on display name. Deactivated users are excluded from the picker
 * (already-inserted chips still render their snapshotted name). All state and
 * keyboard handling live in MentionPopoverStore.
 */

import Box from '@mui/material/Box'
import EventIcon from '@mui/icons-material/Event'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { observer } from 'mobx-react-lite'
import { useRef } from 'react'
import { themeConfig } from '@gap/shared/theme'
import useStore from 'reusable/mobx-store-utils'
import PopoverSurface, { PopoverPosition } from './PopoverSurface'
import { GroupHeader, GroupDivider } from './GroupHeader'
import Avatar from './Avatar'
import { AvatarColorKey } from './avatarColor'
import MentionPopoverStore, { DateOption, MentionPopoverStoreProvider, MentionRenderRow, MentionSelectableRow, useMentionPopoverStore } from './MentionPopoverStore'

export interface MentionUser {
   id: string
   firstName?: string
   lastName?: string
   name?: string
   deactivatedOn?: string | null
}

export type MentionSelection =
   | { kind: 'user'; userId: string; name: string; colorKey: AvatarColorKey; deactivated: boolean }
   | { kind: 'date'; iso: string }
   | { kind: 'date-pick' }

interface MentionPopoverProps {
   anchor: PopoverPosition
   query: string
   team: MentionUser[]
   clientUsers: MentionUser[]
   clientOrgName?: string
   /** Hide the date group (e.g. when the popover is opened from a person chip). */
   hideDates?: boolean
   onSelect: (selection: MentionSelection) => void
   onClose: () => void
}

const MentionPopover = observer(function MentionPopover(props: MentionPopoverProps) {
   const scrollContainerRef = useRef<HTMLDivElement>(null)
   const mentionPopoverStore = useStore(sp => new MentionPopoverStore(sp), { ...props, scrollContainerRef })

   return <MentionPopoverStoreProvider store={mentionPopoverStore}>
      <PopoverSurface anchor={props.anchor} width={296} maxHeight={360} onDismiss={props.onClose}>
         <Box ref={scrollContainerRef} sx={{ overflow: 'auto', flex: 1, padding: '4px 0' }}>
            {mentionPopoverStore.isEmpty
               ? <EmptyPopover />
               : mentionPopoverStore.rows.map(row => (
                  <MentionPopoverRow key={row.key} row={row} />
               ))}
         </Box>
      </PopoverSurface>
   </MentionPopoverStoreProvider>
})

const MentionPopoverRow = observer(function MentionPopoverRow({ row }: { row: MentionRenderRow }) {
   if (row.kind === 'section') {
      return <Box>
         {row.showDivider && <GroupDivider />}
         <GroupHeader>{row.label}</GroupHeader>
      </Box>
   }
   return <MentionPopoverSelectableRow row={row} />
})

const MentionPopoverSelectableRow = observer(function MentionPopoverSelectableRow({ row }: { row: MentionSelectableRow }) {
   const mentionPopoverStore = useMentionPopoverStore()
   const selectableRowStore = useStore(sp => ({
      get isActive() {
         return sp.row.selectableIndex === mentionPopoverStore.activeSelectableIndex
      },
      handleEnter: () => mentionPopoverStore.setActiveSelectableIndex(sp.row.selectableIndex),
      handleClick: (event: React.MouseEvent) => {
         event.preventDefault()
         mentionPopoverStore.handleSelect(sp.row)
      },
   }), { row })

   return <Box data-active={selectableRowStore.isActive ? 'true' : undefined}>
      <Box
         role="option"
         aria-selected={selectableRowStore.isActive}
         onMouseEnter={selectableRowStore.handleEnter}
         onMouseDown={selectableRowStore.handleClick}
         sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 12px',
            minHeight: 36,
            margin: '0 4px',
            borderRadius: '4px',
            fontSize: 14,
            letterSpacing: '0.15px',
            color: themeConfig.primary[800],
            cursor: 'pointer',
            background: selectableRowStore.isActive ? themeConfig.light.action.selected : 'transparent',
            transition: 'background 100ms linear',
         }}
      >
         {row.kind === 'date' ? <DateRowContent option={row.option} /> : <UserRowContent displayName={row.displayName} colorKey={row.colorKey} />}
      </Box>
   </Box>
})

function DateRowContent({ option }: { option: DateOption }) {
   return <>
      <Box sx={{ width: 24, display: 'inline-flex', justifyContent: 'center' }}>
         {option.subtype === 'pick'
            ? <EventIcon sx={{ fontSize: 20, color: themeConfig.primary[500] }} />
            : <CalendarTodayIcon sx={{ fontSize: 18, color: themeConfig.primary[500] }} />}
      </Box>
      <Box sx={{ flex: 1 }}>{option.label}</Box>
      {option.subtype === 'pick' && <ChevronRightIcon sx={{ fontSize: 16, color: themeConfig.primary[500] }} />}
   </>
}

function UserRowContent({ displayName, colorKey }: { displayName: string, colorKey: AvatarColorKey }) {
   return <>
      <Avatar name={displayName} colorKey={colorKey} size={24} />
      <Box sx={{ flex: 1, color: themeConfig.primary[800] }}>{displayName}</Box>
   </>
}

const EmptyPopover = observer(function EmptyPopover() {
   return <Box sx={{ padding: '20px 16px', color: themeConfig.primary[500], fontSize: 13, textAlign: 'center' }}>
      <Box sx={{ marginBottom: '4px', color: themeConfig.primary[800], fontWeight: 500 }}>No matches</Box>
      <Box>Try a different name or date.</Box>
   </Box>
})

export default MentionPopover
